import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Chip,
  Stack,
  Tooltip,
  IconButton
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import {
  GoogleMap,
  Marker,
  InfoWindow,
  OverlayView,
  useJsApiLoader,
} from '@react-google-maps/api';

import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { storeService } from '../services/storeService';

const STORE_PIN_URL = new URL('../../Pin_source.png', import.meta.url).toString();

type DriverDoc = {
  id: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  availability?: string;
  enabled?: boolean;
  location?: { latitude: number; longitude: number } | null;
  lastLocationUpdate?: any;
  locationLastUpdated?: any;
  photoUrl?: string;
};

function parseDriverLocationPayload(data: any): {
  location: { latitude: number; longitude: number };
  lastUpdate?: any;
} | null {
  const loc = data?.currentLocation || data?.location;
  if (!loc || typeof loc.latitude !== 'number' || typeof loc.longitude !== 'number') {
    return null;
  }

  return {
    location: { latitude: loc.latitude, longitude: loc.longitude },
    lastUpdate: data?.lastUpdate || data?.lastLocationUpdate,
  };
}

const DEFAULT_CENTER: [number, number] = [33.5731, -7.5898]; // Casablanca

function getInitials(name?: string): string {
  if (!name) return 'L';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts[1]?.[0] || '';
  return (first + last).toUpperCase() || (first.toUpperCase() || 'L');
}

const containerStyle: React.CSSProperties = { width: '100%', height: '100%' };

// Smoothly interpolate marker position between Firestore updates to avoid jumps
function useAnimatedLatLng(target: { lat: number; lng: number }, durationMs = 700) {
  const [pos, setPos] = useState<{ lat: number; lng: number } | null>(null);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number>(0);
  const fromRef = useRef<{ lat: number; lng: number } | null>(null);
  const toRef = useRef<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    // First-time: snap to target
    if (!pos) {
      setPos(target);
      fromRef.current = target;
      toRef.current = target;
      return;
    }

    // Cancel any running animation
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    // Start new animation from current pos to target
    startRef.current = performance.now();
    fromRef.current = pos;
    toRef.current = target;

    const easeInOutCubic = (t: number) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const tick = (now: number) => {
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / durationMs);
      const eased = easeInOutCubic(t);

      const from = fromRef.current!;
      const to = toRef.current!;
      const lat = from.lat + (to.lat - from.lat) * eased;
      const lng = from.lng + (to.lng - from.lng) * eased;
      setPos({ lat, lng });

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target.lat, target.lng]); // re-run when target changes

  return pos ?? target;
}

function DriverOverlay({
  driver,
  onSelect,
}: {
  driver: DriverDoc;
  onSelect: (payload: { d: DriverDoc; pos: { lat: number; lng: number } }) => void;
}) {
  const displayName = `${driver.firstName || ''} ${driver.lastName || ''}`.trim() || 'Livreur';
  const targetPos = { lat: driver.location!.latitude, lng: driver.location!.longitude };
  const animatedPos = useAnimatedLatLng(targetPos, 700);

  return (
    <React.Fragment>
      <OverlayView
        key={`${driver.id}-label`}
        position={animatedPos}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={(width?: number, height?: number) => {
          const w = width ?? 0;
          const h = height ?? 0;
          const avatarH = 44;
          const overlap = 30;
          return { x: -w / 2, y: -(avatarH - overlap + h) };
        }}
      >
        <div
          style={{
            fontWeight: 700,
            color: '#000000',
            background: 'transparent',
            borderRadius: 0,
            padding: 0,
            boxShadow: 'none',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            lineHeight: 1.1,
            fontSize: '14px',
            textShadow: '0 0 3px #ffffff, 0 0 6px #ffffff, 0 0 12px #ffffff',
            userSelect: 'none',
          }}
        >
          {displayName}
        </div>
      </OverlayView>

      <OverlayView
        key={`${driver.id}-avatar`}
        position={animatedPos}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        getPixelPositionOffset={(width?: number, height?: number) => {
          const w = width ?? 44;
          const h = height ?? 44;
          return { x: -w / 2, y: -h };
        }}
      >
        <div
          onClick={() => onSelect({ d: driver, pos: targetPos })}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            width: 44,
            height: 44,
          }}
        >
          {driver.photoUrl ? (
            <img
              src={driver.photoUrl}
              alt={displayName}
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: '2px solid #fff',
                boxShadow: '0 0 6px rgba(0,0,0,.35)',
                objectFit: 'cover',
                background: '#eee',
                display: 'block',
                boxSizing: 'border-box',
                aspectRatio: '1 / 1',
              }}
            />
          ) : (
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: '#1abc9c',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                border: '2px solid #fff',
                boxShadow: '0 0 6px rgba(0,0,0,.35)',
              }}
            >
              {getInitials(displayName)}
            </div>
          )}
        </div>
      </OverlayView>
    </React.Fragment>
  );
}

const DriversLiveMapModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [drivers, setDrivers] = useState<DriverDoc[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [store, setStore] = useState<{ lat: number; lng: number; name: string } | null>(null);

  const [storeInfoOpen, setStoreInfoOpen] = useState<boolean>(false);
  const [selectedDriver, setSelectedDriver] = useState<{ d: DriverDoc; pos: { lat: number; lng: number } } | null>(null);

  const mapRef = useRef<any>(null);
  const hasFittedRef = useRef<boolean>(false);
  const locationUnsubsRef = useRef<Map<string, () => void>>(new Map());

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  });

  useEffect(() => {
    if (!open) return;
    const locationUnsubs = locationUnsubsRef.current;

    // Subscribe to online drivers; filter location client-side for reliability
    const col = collection(db, 'drivers');
    const q = query(col, where('availability', '==', 'online'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: DriverDoc[] = [];
        snap.forEach((d) => {
          const data = d.data() as any;
          const payload = parseDriverLocationPayload(data);
          list.push({
            id: d.id,
            firstName: data?.firstName,
            lastName: data?.lastName,
            phone: data?.phone,
            availability: data?.availability,
            enabled: data?.enabled,
            location: payload?.location || null,
            lastLocationUpdate: payload?.lastUpdate,
            locationLastUpdated: payload?.lastUpdate || data?.locationLastUpdated,
            photoUrl: data?.photoUrl,
          });
        });
        setDrivers(list);

        const nextDriverIds = new Set(list.map((driver) => driver.id));
        const staleDriverIds: string[] = [];
        locationUnsubs.forEach((_unsubscribe, driverId) => {
          if (!nextDriverIds.has(driverId)) {
            staleDriverIds.push(driverId);
          }
        });
        staleDriverIds.forEach((driverId) => {
          const unsubscribe = locationUnsubs.get(driverId);
          unsubscribe?.();
          locationUnsubs.delete(driverId);
        });

        list.forEach((driver) => {
          if (locationUnsubs.has(driver.id)) return;
          const unsubscribe = onSnapshot(doc(db, 'driver_locations', driver.id), (locationSnap) => {
            const payload = locationSnap.exists()
              ? parseDriverLocationPayload(locationSnap.data())
              : null;
            if (!payload) return;

            setDrivers((previous) =>
              previous.map((item) =>
                item.id === driver.id
                  ? {
                      ...item,
                      location: payload.location,
                      lastLocationUpdate: payload.lastUpdate,
                      locationLastUpdated: payload.lastUpdate,
                    }
                  : item
              )
            );
          });
          locationUnsubs.set(driver.id, unsubscribe);
        });
        setLoading(false);
      },
      (err) => {
        console.error('Error subscribing drivers live map:', err);
        setLoading(false);
      }
    );

    return () => {
      unsub();
      locationUnsubs.forEach((unsubscribe) => unsubscribe());
      locationUnsubs.clear();
    };
  }, [open]);

  // Fetch delivery-responsible store location
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const s = await storeService.getDeliveryResponsibleStore();
        if (s?.location) {
          setStore({ lat: s.location.latitude, lng: s.location.longitude, name: s.name || 'Magasin' });
        } else {
          setStore(null);
        }
      } catch {
        setStore(null);
      }
    })();
  }, [open]);

  const driversWithLocation = useMemo(
    () => drivers.filter((d) => d.location && typeof d.location.latitude === 'number' && typeof d.location.longitude === 'number'),
    [drivers]
  );

  const positions: [number, number][] = useMemo(
    () => driversWithLocation.map((d) => [d.location!.latitude, d.location!.longitude]),
    [driversWithLocation]
  );

  const allPositions: [number, number][] = useMemo(() => {
    if (store) return [...positions, [store.lat, store.lng]];
    return positions;
  }, [positions, store]);

  const center = positions[0] ?? DEFAULT_CENTER;

  // Fit bounds once on first render after map/positions ready
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    if (hasFittedRef.current) return;
    if (allPositions.length === 0) return;
    recenterAll();
    hasFittedRef.current = true;
  }, [isLoaded, allPositions]);

  const openAllInGoogleMaps = () => {
    if (positions.length === 0) return;
    const url = `https://www.google.com/maps/dir/${positions.map((p) => `${p[0]},${p[1]}`).join('/')}/`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const recenterAll = () => {
    if (!isLoaded || !mapRef.current) return;
    if (allPositions.length === 0) return;

    const map = mapRef.current as any;
    if (allPositions.length === 1) {
      const [lat, lng] = allPositions[0];
      map.setCenter({ lat, lng });
      map.setZoom(14);
      hasFittedRef.current = true;
      return;
    }

    const bounds = new (window as any).google.maps.LatLngBounds();
    allPositions.forEach(([lat, lng]) => bounds.extend({ lat, lng }));
    const padding = { top: 50, right: 50, bottom: 50, left: 50 };
    try {
      map.fitBounds(bounds, padding as any);
    } catch {
      map.fitBounds(bounds);
    }
    hasFittedRef.current = true;
  };


  const centerObj = Array.isArray(center)
    ? { lat: center[0] as number, lng: center[1] as number }
    : { lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '90vh' } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">Localisation des Livreurs (temps réel)</Typography>
            <Chip
              color="success"
              size="small"
              label={`${driversWithLocation.length} en ligne avec position`}
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button onClick={recenterAll} size="small" variant="outlined" disabled={allPositions.length === 0}>
              Recentrer
            </Button>
            <Tooltip title="Voir toutes les localisations sur Google Maps">
              <span>
                <IconButton onClick={openAllInGoogleMaps} disabled={driversWithLocation.length === 0} color="primary">
                  <OpenInNewIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Fermer">
              <span>
                <IconButton onClick={onClose} color="default">
                  <CloseIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
          {!isLoaded ? (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">Chargement de la carte…</Typography>
            </Box>
          ) : (
            <>
            <GoogleMap
              onLoad={(map) => {
                mapRef.current = map;
              }}
              center={centerObj}
              zoom={positions.length > 0 ? 13 : 12}
              mapContainerStyle={containerStyle}
              options={{
                mapTypeId: 'roadmap',
                streetViewControl: false,
                fullscreenControl: true,
                gestureHandling: 'greedy',
              }}
            >
              {/* Store marker using custom icon; no permanent label, only InfoWindow on click */}
              {store && (
                <>
                  <Marker
                    position={{ lat: store.lat, lng: store.lng }}
                    onClick={() => setStoreInfoOpen(true)}
                    options={{
                      zIndex: 1000,
                      icon: {
                        url: STORE_PIN_URL,
                        scaledSize: (window as any).google
                          ? new (window as any).google.maps.Size(40, 40)
                          : undefined,
                        anchor: (window as any).google
                          ? new (window as any).google.maps.Point(20, 38)
                          : undefined,
                      } as any,
                    }}
                  />
                  {storeInfoOpen && (
                    <InfoWindow
                      position={{ lat: store.lat, lng: store.lng }}
                      onCloseClick={() => setStoreInfoOpen(false)}
                      options={{ disableAutoPan: false }}
                    >
                      <div>
                        <Typography variant="subtitle2" component="div">{store.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {store.lat.toFixed(5)}, {store.lng.toFixed(5)}
                        </Typography>
                      </div>
                    </InfoWindow>
                  )}
                </>
              )}

              {/* Drivers: avatar overlay + permanent name label, click opens InfoWindow */}
              {driversWithLocation.map((d) => (
                <DriverOverlay key={d.id} driver={d} onSelect={(payload) => setSelectedDriver(payload)} />
              ))}

              {selectedDriver && (
                <InfoWindow
                  position={selectedDriver.pos}
                  onCloseClick={() => setSelectedDriver(null)}
                  options={{ disableAutoPan: false }}
                >
                  <div>
                    <Typography variant="subtitle2" component="div">
                      {`${selectedDriver.d.firstName || ''} ${selectedDriver.d.lastName || ''}`.trim() || 'Livreur'}
                    </Typography>
                    {selectedDriver.d.phone ? (
                      <Typography variant="body2" color="text.secondary">{selectedDriver.d.phone}</Typography>
                    ) : null}
                    <Typography variant="body2" color="text.secondary">
                      {selectedDriver.pos.lat.toFixed(5)}, {selectedDriver.pos.lng.toFixed(5)}
                    </Typography>
                    {(() => {
                      const lastTs =
                        (selectedDriver.d.lastLocationUpdate as any)?.toDate?.() ||
                        (selectedDriver.d.locationLastUpdated as any)?.toDate?.();
                      return lastTs ? (
                        <Typography variant="caption" color="text.secondary">
                          Dernière mise à jour: {new Date(lastTs).toLocaleString()}
                        </Typography>
                      ) : null;
                    })()}
                  </div>
                </InfoWindow>
              )}
              </GoogleMap>
            </>
            )}
          </Box>

      </DialogContent>

    </Dialog>
  );
};

export default DriversLiveMapModal;
